import React, { ChangeEvent, FC, ReactElement, useState } from 'react';
import Button from '../../ui/Button/BlockButton';
import TextArea from '../../ui/TextArea/TextArea';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import PanelContentHeader from '../../ui/PanelContentHeader/PanelContentHeader';
import StarRating from './StarRating/StarRating';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import styles from './LandscapeExpanded.module.css';

export interface LandscapeExpandedProps {
  featureName?: string;
  selectedRating: number;
  onRatingSelect: (index: number) => void;
  onClickSupportLink?: () => void;
  submitFeedback?: (feedbackComment: string) => void;
  commentOptional?: boolean;
  enablePanelHeader: boolean;
}

const LEFT = 'left';

const LandscapeExpanded: FC<LandscapeExpandedProps> = ({
  featureName,
  selectedRating,
  onRatingSelect,
  onClickSupportLink,
  submitFeedback,
  commentOptional,
  enablePanelHeader,
}): ReactElement => {
  const {
    feedbackButtonText = '',
    feedbackButtonTextShort = '',
    feedbackCommentLabel = '',
    feedbackCustomerSupportLink = '',
    feedbackExperienceHeader = '',
    feedbackExperienceBodyText = '',
  } = useTranslations();

  const {
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle,
  } = useTheme();

  const { feedback } = useSettings();

  const { customerSupportLink } = feedback ?? {};

  const [feedbackComment, setFeedbackComment] = useState('');
  const [interactiveExperienceHeader, setInteractiveExperienceHeader] =
    useState(feedbackExperienceHeader);

  return (
    <div className={styles.landscapeExpandedContainer}>
      <div
        className={styles.feedbackTextContainer}
        style={{ gridColumn: 'span 5' }}
      >
        <PanelContentHeader
          as={enablePanelHeader ? 'p' : 'h2'}
          featureName={featureName}
          headerText={
            interactiveExperienceHeader ?? feedbackExperienceHeader ?? ''
          }
          paragraphChildren={
            <>
              <span className={styles.feedbackBox}>
                {feedbackExperienceBodyText}
              </span>
              {customerSupportLink && (
                <span className={styles.csLink}>
                  <TextLink
                    target={TextLinkTargetType.BLANK}
                    href={customerSupportLink}
                    linkStyle={{
                      sDefault: {
                        textAlign: LEFT,
                        ...textLinkStyle,
                      },
                      sHover: textLinkHoverStyle,
                    }}
                    onClick={() => {
                      if (onClickSupportLink) onClickSupportLink();
                    }}
                  >
                    {feedbackCustomerSupportLink}
                  </TextLink>
                </span>
              )}
            </>
          }
          isLandscape
        />
      </div>
      <div
        className={styles.startRatingContainer}
        style={{ gridColumn: 'span 7' }}
      >
        <StarRating
          onClick={(index: number, selectedText?: string) => {
            onRatingSelect(index);
            setInteractiveExperienceHeader(
              selectedText ?? feedbackExperienceHeader,
            );
          }}
          selectedRating={selectedRating}
          featureName={featureName}
          isLandscape
        />
        <div className={styles.feedbackBox}>
          <TextArea
            id="feedbackCommentBox"
            animateLabel
            label={feedbackCommentLabel ?? ''}
            featureName={featureName}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setFeedbackComment(event.target.value)
            }
          />
        </div>
        <div className={styles.feedbackButton}>
          <Button
            disabled={!commentOptional && !feedbackComment.length}
            big
            onClick={() => {
              if (submitFeedback) submitFeedback(feedbackComment);
            }}
          >
            {feedbackButtonTextShort || feedbackButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandscapeExpanded;
